import { useState, useRef, useCallback } from "react";
import {
  Stack,
  Typography,
  TextField,
  IconButton,
  Divider,
  Box,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Tooltip
} from "@material-ui/core";
import { Add, Delete, Group } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { fDateTimeSuffix } from "utils/formatTime";
import useSettings from "hooks/useSettings";
import { useKeepAboveKeyboard } from "hooks/useKeepAboveKeyboard";
import { useSellerNotes } from "./useSellerNotes";
import type { DisplayUserNote } from "hooks/useListingNotes";

type QuickNotesInlineProps = {
  contactId: string;
};

export function QuickNotesInline({ contactId }: QuickNotesInlineProps) {
  const { t } = useTranslation();
  const { themeDirection } = useSettings();
  const isRTL = themeDirection === "rtl";

  const { notes, isLoading, addNote, updateNote, deleteNote, setNoteOrgShare } = useSellerNotes(
    contactId,
    true
  );

  const [newNoteText, setNewNoteText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleAddNote = useCallback(() => {
    if (!newNoteText.trim()) return;
    addNote(newNoteText);
    setNewNoteText("");
    inputRef.current?.focus();
  }, [newNoteText, addNote]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleAddNote();
      }
    },
    [handleAddNote]
  );

  /**
   * Same soft-keyboard problem as the popover, minus the popover: this variant renders in
   * the old contacts table's expanded row, so the input sits wherever that row happens to
   * be — routinely below the fold on a phone, and covered the moment the keyboard opens.
   *
   * `enabled` is unconditionally true because there is no open/closed state to gate on; the
   * hook does nothing until the input it tracks actually holds focus, and nothing at all on
   * desktop, where the visual viewport never shrinks. Nothing here auto-focuses, so the
   * `focusin` leg is what does the work.
   */
  useKeepAboveKeyboard(inputRef, true);

  return (
    <Stack spacing={1.5}>
      {/* Add new note */}
      <Stack direction="row" gap={0.5} alignItems="flex-start">
        <TextField
          inputRef={inputRef}
          fullWidth
          size="small"
          multiline
          maxRows={3}
          placeholder={t("rentalToSale.notes.placeholder")}
          value={newNoteText}
          onChange={(e) => setNewNoteText(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <IconButton
          size="small"
          color="primary"
          onClick={handleAddNote}
          disabled={!newNoteText.trim()}
        >
          <Add fontSize="small" />
        </IconButton>
      </Stack>

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {/* Existing notes */}
      {notes.map((note) => (
        <InlineNoteItem
          key={note.id}
          note={note}
          isRTL={isRTL}
          onUpdate={updateNote}
          onDelete={deleteNote}
          onSetOrgShare={setNoteOrgShare}
        />
      ))}
    </Stack>
  );
}

function InlineNoteItem({
  note,
  isRTL,
  onUpdate,
  onDelete,
  onSetOrgShare
}: {
  note: DisplayUserNote;
  isRTL: boolean;
  onUpdate: (noteId: string, text: string) => void;
  onDelete: (noteId: string) => void;
  onSetOrgShare?: (noteId: string, share: boolean) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(note.note);

  const handleBlur = useCallback(() => {
    setEditing(false);
    if (editText.trim() !== note.note) {
      if (editText.trim()) {
        onUpdate(note.id, editText);
      } else {
        onDelete(note.id);
      }
    }
  }, [editText, note, onUpdate, onDelete]);

  const isShared = (note.organizationIds?.length ?? 0) > 0;
  const isMine = note.isMine ?? true;

  return (
    <Stack
      sx={{
        p: 1,
        borderRadius: 1,
        // Tint teammates' notes so they read as distinct from own ones —
        // matches QuickNotesPopover's treatment.
        bgcolor: isMine ? "action.hover" : "info.lighter",
        "&:hover .note-delete": { opacity: 1 }
      }}
      spacing={0.5}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        {editing && isMine ? (
          <TextField
            fullWidth
            size="small"
            multiline
            maxRows={4}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onBlur={handleBlur}
            autoFocus
          />
        ) : (
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              cursor: isMine ? "pointer" : "default",
              ...(isMine && {
                "&:hover": { bgcolor: "action.selected", borderRadius: 0.5 }
              })
            }}
            onClick={
              isMine
                ? () => {
                    setEditing(true);
                    setEditText(note.note);
                  }
                : undefined
            }
          >
            {note.note}
          </Typography>
        )}
        {!editing && isMine && (
          <IconButton
            className="note-delete"
            size="small"
            sx={{ opacity: 0, transition: "opacity 0.15s", ml: 0.5 }}
            onClick={() => onDelete(note.id)}
          >
            <Delete sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={0.5}
      >
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {fDateTimeSuffix(note.noteTime, isRTL)}
          </Typography>
          {!isMine && note.authorName && (
            <Typography
              variant="caption"
              sx={{ color: "info.dark", fontWeight: 500 }}
              noWrap
            >
              · {note.authorName}
            </Typography>
          )}
        </Stack>
        {onSetOrgShare && isMine && (
          <Tooltip
            title={
              isShared
                ? t("userNotes.sharedWithOrgTooltip")
                : t("userNotes.shareWithOrgTooltip")
            }
          >
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={isShared}
                  onChange={(_, checked) => onSetOrgShare(note.id, checked)}
                  icon={<Group sx={{ fontSize: 14, color: "text.disabled" }} />}
                  checkedIcon={<Group sx={{ fontSize: 14, color: "primary.main" }} />}
                  sx={{ p: 0.25 }}
                />
              }
              label={
                <Typography
                  variant="caption"
                  sx={{ color: isShared ? "primary.main" : "text.secondary" }}
                >
                  {isShared ? t("userNotes.sharedWithOrg") : t("userNotes.private")}
                </Typography>
              }
            />
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
}
